import { useState, useMemo } from 'react'
import { Box } from '@mui/material'
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../../shared/hooks/useIntractionTranslate'
import { Button, Input } from '../../interaxDesignSystemComponents'
import { FAQItem, FAQCategory } from '../../types/FAQTypes'
import { INTERAX_COLORS } from '../../constants/colors'
import FAQInfoBox from './FAQInfoBox'
import { filterFAQsWithAnswers, getUniqueFAQCategories } from '../../shared/utils/faqHelpers'

interface FAQAskedQuestionsProps<T extends FAQItem = FAQItem> {
  faqItems: T[]
  titleKey?: string
  searchPlaceholderKey?: string
  noResultsKey?: string
}

export default function FAQAskedQuestions<T extends FAQItem>({
  faqItems,
  titleKey = 'faq.frequentlyAskedQuestions',
  searchPlaceholderKey = 'faq.searchPlaceholder',
  noResultsKey = 'faq.noResultsFound'
}: FAQAskedQuestionsProps<T>) {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const [activeCategory, setActiveCategory] = useState<FAQCategory>(FAQCategory.ALL)

  // Filter out FAQs with missing answers
  const faqsWithAnswers = useMemo(() => {
    return filterFAQsWithAnswers(faqItems, t)
  }, [faqItems, t])

  // Get unique categories from FAQs that have answers
  const availableCategories = useMemo(() => {
    const categories = getUniqueFAQCategories(faqsWithAnswers) as FAQCategory[]
    return [FAQCategory.ALL, ...categories]
  }, [faqsWithAnswers])

  // Filter FAQs based on search term and category
  const filteredFAQs = useMemo(() => {
    let filtered = faqsWithAnswers

    // Filter by category (check if any of the FAQ's categories match the active category)
    if (activeCategory !== FAQCategory.ALL) {
      filtered = filtered.filter((faq) => faq.categories.includes(activeCategory))
    }

    // Filter by search term (search in translated question and description)
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter((faq) => {
        const question = t(faq.questionKey).toLowerCase()
        const description = t(faq.descriptionKey).toLowerCase()
        return question.includes(searchLower) || description.includes(searchLower)
      })
    }

    return filtered
  }, [faqsWithAnswers, activeCategory, searchTerm, t])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)

    // When user types something, automatically deselect filters and select all
    if (value.trim() && activeCategory !== FAQCategory.ALL) {
      setActiveCategory(FAQCategory.ALL)
    }
  }

  const handleClearSearch = () => {
    setSearchTerm('')
  }

  const handleCategoryChange = (category: FAQCategory) => {
    setActiveCategory(category)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        padding: '24px',
        borderRadius: '8px',
        backgroundColor: INTERAX_COLORS.heritageBlue40
      }}
    >
      {/* Title */}
      <Box
        component="h2"
        sx={{
          fontFamily: 'Lexend Deca, sans-serif',
          fontWeight: 600,
          fontSize: '20px',
          lineHeight: '28px',
          color: INTERAX_COLORS.white,
          m: 0
        }}
      >
        {t(titleKey)}
      </Box>

      {/* Search Input */}
      <Box sx={{ width: '100%' }}>
        <Input
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder={t(searchPlaceholderKey)}
          type="search"
          size="small"
          fullWidth
          startIcon={<MagnifyingGlassIcon style={{ width: 20, height: 20, color: INTERAX_COLORS.thunderGrey }} />}
          endIcon={
            searchTerm ? <XMarkIcon style={{ width: 20, height: 20, color: INTERAX_COLORS.thunderGrey }} /> : undefined
          }
          onEndIconClick={searchTerm ? handleClearSearch : undefined}
        />
      </Box>

      {/* Filter Buttons */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        {availableCategories.map((category) => (
          <Button
            key={category}
            variant={activeCategory === category ? 'primary' : 'secondary'}
            onClick={() => handleCategoryChange(category)}
          >
            {t(`faq.categories.${category}`)}
          </Button>
        ))}
      </Box>

      {/* FAQ Items Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 3
        }}
      >
        {filteredFAQs.map((faq) => (
          <FAQInfoBox key={faq.id} faq={faq} />
        ))}
      </Box>

      {/* No Results */}
      {filteredFAQs.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Box
            component="p"
            sx={{
              fontFamily: 'Lexend Deca, sans-serif',
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '24px',
              color: INTERAX_COLORS.thunderGrey,
              m: 0
            }}
          >
            {t(noResultsKey)}
          </Box>
        </Box>
      )}
    </Box>
  )
}
